/**
 * Definição do menu lateral da aplicação.
 *
 * `buildMenu` recebe o componente que o consome (`App.vue`) e devolve o modelo de
 * menu que o `AppSubmenu` renderiza. As funções `visible` são avaliadas em runtime,
 * a cada render, pelo que dependem do `ctx` recebido aqui.
 *
 * Regras:
 *  - A visibilidade de um item é sempre uma ou mais ACLs (`anyOf`). Não há
 *    bypass: nem `currentUser.isEsa`, nem `department`.
 *  - A visibilidade de um grupo deriva dos filhos (`group`), para que nenhum grupo
 *    abra vazio e para que um filho nunca fique inacessível por o pai ser mais
 *    restritivo do que ele.
 *    Exception (requested): «Operações › Mapas Interativos» also requires
 *    `seeInteractiveMapsMenu`, so its maps stay hidden from the menu until
 *    that alias is granted (the pages still open by URL).
 *  - Os aliases NUNCA se renomeiam: os grants vivem em `group_alcs` e um alias
 *    renomeado é um alias revogado.
 *  - `keywords` é a lista de nomes antigos/sinónimos de um item, usada só pela
 *    pesquisa de páginas da sidebar (src/helpers/menuSearch.js). Quando se
 *    renomeia um item, o nome antigo vai para aqui.
 */

import { APPROVAL_ACLS, APPROVAL_ITEMS } from "./approvals";

// Shared by both companies' «Hub Financeiro»; the old item name stays here
// so the sidebar search still finds it.
const HUB_FINANCEIRO_KEYWORDS = [
  "Hub Financeiro",
  "faturação",
  "recebimentos",
  "compras",
  "recorrente",
  "MRR",
  // The commission payments moved in from Comercial › Comissões.
  "comissões de contratos",
  "gestão de pagamentos",
];

export const isItemVisible = (item) =>
  typeof item.visible === "function" ? item.visible() : item.visible !== false;

export function buildMenu(ctx) {
  const can = (alias) => ctx.can(alias);
  const anyOf = (...aliases) => aliases.some(can);

  /**
   * Grupo de menu cuja visibilidade deriva EXCLUSIVAMENTE dos filhos: um grupo
   * nunca abre vazio, e um filho nunca fica escondido por o pai ser mais
   * restritivo do que ele. Em consequência, os aliases que só serviam para
   * mostrar/esconder um grupo (`seeHelpdeskMenu`, `seeAclsMenu`, ...) deixam de
   * ter efeito — ver a nota no fim do ficheiro.
   */
  const group = (label, icon, items) => ({
    label,
    icon,
    items,
    visible: () => items.some(isItemVisible),
  });

  // Each map keeps its own ACL; the group adds «seeInteractiveMapsMenu».
  const interactiveMaps = [
    {
      label: "Instalações",
      icon: "pi pi-fw pi-sliders-h",
      to: "/reports/installations-map",
      visible: () => anyOf("seeInstallationsMap"),
      keywords: [
        "Mapa Interativo Instalações",
        "mapa",
        "instalações",
        "concelhos",
        "distritos",
      ],
    },
    {
      label: "Intervenções",
      icon: "pi pi-fw pi-wrench",
      to: "/reports/interventions-map",
      visible: () => anyOf("seeInterventionsMap"),
      keywords: [
        "Mapa Interativo Intervenções",
        "mapa",
        "intervenções",
        "técnicos",
        "concelhos",
        "distritos",
      ],
    },
    {
      label: "Equipamentos",
      icon: "pi pi-fw pi-box",
      to: "/reports/equipment-map",
      visible: () => anyOf("seeEquipmentMap"),
      keywords: [
        "Mapa do Parque Instalado",
        "Mapa de Equipamentos",
        "mapa",
        "equipamentos",
        "parque instalado",
      ],
    },
  ];

  // Os Surveys são pedidos pelo Comercial e executados pelo Suporte: o nó é
  // definido uma vez e referenciado nos dois grupos, de propósito.
  const surveys = {
    label: "Surveys",
    icon: "pi pi-fw pi-bolt",
    to: "/surveys",
    visible: () => anyOf("seeSurveys"),
  };

  return [
    {
      label: "Início",
      icon: "pi pi-fw pi-home",
      to: "/",
      keywords: ["Home"],
    },
    {
      label: "O Que Tenho a Fazer",
      icon: "pi pi-fw pi-check-square",
      to: "/my-work",
    },

    group("O Meu Espaço", "pi pi-fw pi-user", [
      {
        label: "Horas",
        icon: "pi pi-fw pi-clock",
        to: "/hours",
        visible: () => anyOf("seeMenuHours"),
      },
      {
        label: "Os Meus Indicadores",
        icon: "pi pi-fw pi-chart-bar",
        to: "/indicators",
        keywords: ["Dashboard", "KPI"],
      },
      {
        label: "As Minhas Notificações",
        icon: "pi pi-fw pi-bell",
        to: "/my-notifications",
        keywords: ["Centro de notificações", "Dashboard de notificações"],
      },
      {
        label: "Registo de Ponto",
        icon: "pi pi-fw pi-clock",
        to: "/time-clock",
        visible: () => anyOf("punchTimeClock"),
      },
      {
        label: "As Minhas Férias",
        icon: "pi pi-fw pi-slack",
        to: "/vacations",
        visible: () => anyOf("seeMyvacation"),
      },
      {
        label: "As Minhas Faltas",
        icon: "pi pi-fw pi-user-minus",
        to: "/miss",
        visible: () => anyOf("seeMiss"),
      },
      {
        label: "As Minhas Despesas",
        icon: "pi pi-fw pi-euro",
        to: "/expenditure",
        visible: () => anyOf("seeExpenditures"),
      },
      {
        label: "Os Meus Equipamentos",
        icon: "pi pi-fw pi-mobile",
        to: "/my-equipment",
        keywords: ["equipamentos", "material", "telemóvel", "portátil", "ferramentas"],
      },
      {
        label: "Ajudas de Custo",
        icon: "pi pi-fw pi-dollar",
        to: "/costAssistence",
        visible: () => anyOf("seeCostAssistence"),
      },
      {
        label: "Horas Extra",
        icon: "pi pi-fw pi-clock",
        to: "/extra-hours",
        visible: () => anyOf("seeExtraHours"),
      },
      {
        label: "Dias por Fechar",
        icon: "pi pi-fw pi-calendar-times",
        to: "/open-days-summary",
        visible: () => anyOf("seeOpenDaysSummary"),
      },
      {
        label: "As Minhas Indisponibilidades",
        icon: "pi pi-user-minus",
        to: "/booking/unavailability",
      },
      {
        label: "As Minhas Formações",
        icon: "pi pi-fw pi-bookmark-fill",
        to: "/my-training",
        visible: () => anyOf("seeMyTraining"),
      },
      {
        label: "As Minhas Competências",
        icon: "pi pi-fw pi-star",
        to: "/competency-profile-user",
      },
      {
        label: "Documentos Internos",
        icon: "pi pi-fw pi-info",
        to: "/internal-documents",
      },
    ]),

    // One page for every approval; each item inside keeps the aliases of its
    // old menu entry (config/approvals.js), whose names stay searchable here.
    {
      label: "Aprovações",
      icon: "pi pi-fw pi-check-square",
      to: "/approvals",
      keywords: [
        ...APPROVAL_ITEMS.map((item) => item.label),
        "Indicadores",
        "definições de despesas",
        "limite almoço",
        "limite jantar",
        "Aprovar/Processar",
      ],
      visible: () => anyOf(...APPROVAL_ACLS),
    },

    group("Tarefas", "pi pi-fw pi-table", [
      {
        label: "Task Board",
        icon: "pi pi-fw pi-table",
        to: "/task-board",
        visible: () => anyOf(
          "canAccessTaskboard",
          "taskboardFullAccess",
          "seeFleetVehicleDetails",
          "frotaTicketsManage"
        ),
      },
      {
        label: "Task Board Instalações",
        icon: "pi pi-fw pi-wrench",
        to: "/installations-task-board",
        visible: () =>
          anyOf("viewInstallationsTaskboard", "manageInstallationsTaskboard"),
      },
      {
        label: "Projetos",
        icon: "pi pi-fw pi-folder",
        to: "/task-board/projects",
        visible: () => anyOf("canCreateTaskboardProjects", "taskboardFullAccess"),
      },
    ]),

    group("Suporte", "pi pi-fw pi-wrench", [
      {
        label: "Agenda",
        icon: "pi pi-fw pi-calendar",
        to: "/support",
        keywords: ["Agenda - Suporte"],
        visible: () => anyOf("seeSupportSchedule"),
      },
      {
        label: "Mapa de Intervenções",
        icon: "pi pi-map-marker",
        to: "/support-map",
        visible: () => anyOf("seeInterventionMap"),
      },
      {
        label: "Reservas",
        icon: "pi pi-calendar",
        to: "/support/bookings",
        visible: () => anyOf("seeBookinglistMenu"),
      },
      group(
        "Helpdesk",
        "pi pi-fw pi-comments",
        [
          {
            label: "Tickets",
            icon: "pi pi-fw pi-ticket",
            to: "/helpdesk-tickets",
            visible: () => anyOf("seeTickets"),
          },
          {
            label: "Contactos",
            icon: "pi pi-fw pi-id-card",
            to: "/helpdesk-contacts",
            visible: () => anyOf("seeHelpdeskContacts"),
          },
          {
            label: "Equipas de Suporte",
            icon: "pi pi-fw pi-users",
            to: "/support-teams",
            visible: () => anyOf("viewSupportTeams"),
          },
          {
            label: "Emails Ignorados",
            icon: "pi pi-fw pi-envelope",
            to: "/helpdesk-blocked-emails",
            visible: () => anyOf("seeBlockedEmails"),
          },
          {
            label: "Assuntos Ignorados",
            icon: "pi pi-fw pi-list",
            to: "/helpdesk-blocked-subjects",
            visible: () => anyOf("seeBlockedSubjects"),
          },
        ]
    ),
      group("Surveys", "pi pi-fw pi-bolt", [
        surveys,
        {
          label: "Áreas de Survey",
          icon: "pi pi-user-plus",
          to: "/operational-management/survey-pats",
          keywords: ["Surveys Areas"],
          visible: () => anyOf("seeSurveyPatsList"),
        },
      ]),
      group("PATs", "pi pi-fw pi-list", [
        {
          label: "Todos os PATs",
          icon: "pi pi-fw pi-list",
          to: "/pats-list",
          visible: () => anyOf("seePatsList"),
        },
        {
          label: "PATs da Minha Equipa",
          icon: "pi pi-fw pi-users",
          to: "/my-team-pats",
          keywords: ["Os Meus PATs"],
          visible: () => anyOf("seeMyTeamPats"),
        },
        {
          label: "PATs Internos",
          icon: "pi pi-fw pi-home",
          to: "/internal-pats",
          visible: () => anyOf("seeInternalPats"),
        },
        {
          label: "Intervenções",
          icon: "pi pi-fw pi-wrench",
          to: "/interventions-list",
          visible: () => anyOf("viewPatInterventions"),
        },
      ]),
      {
        label: "Instalações",
        icon: "pi pi-fw pi-sliders-h",
        to: "/installations",
        visible: () => anyOf("seeInstallations"),
      },
      {
        label: "Planeador de Instalações",
        icon: "pi pi-fw pi-calendar",
        to: "/installation-planner",
        visible: () => anyOf("manageInstallationPlanner"),
      },
      {
        label: "Assistências",
        icon: "pi pi-fw pi-thumbs-up-fill",
        to: "/assists",
        visible: () => anyOf("seeAssists"),
      },
      group(
        "Inquéritos de Satisfação",
        "pi pi-fw pi-check-circle",
        [
          {
            label: "Instalações",
            icon: "pi pi-fw pi-sliders-h",
            to: "/satisfaction-form-installations",
            visible: () => anyOf("seeInstallationSurveys"),
          },
          {
            label: "Instalações (Exportar)",
            icon: "pi pi-fw pi-external-link",
            to: "/satisfaction-form-installations-export",
            keywords: ["Exportar Inq. Instalações"],
            visible: () => anyOf("seeExportInstallationSurveys"),
          },
          {
            label: "Assistências",
            icon: "pi pi-fw pi-paypal",
            to: "/satisfaction-form-support",
            visible: () => anyOf("seeAssistSurveys"),
          },
          {
            label: "Assistências (Exportar)",
            icon: "pi pi-fw pi-external-link",
            to: "/satisfaction-form-support-export",
            keywords: ["Exportar Inq. Assistências"],
            visible: () => anyOf("seeExportAssistSurveys"),
          },
          {
            // Alias emprestado das instalações: não existe um alias próprio
            // para o resumo, e criar um obrigaria a atribuí-lo nos grupos.
            label: "Resumo",
            icon: "pi pi-fw pi-table",
            to: "/satisfaction-form-resume",
            visible: () => anyOf("seeExportInstallationSurveys"),
          },
        ]
    ),
      group("Unidashboard", "pi pi-fw pi-exclamation-triangle", [
        {
          label: "Ocorrências",
          icon: "pi pi-fw pi-exclamation-triangle",
          to: "/unidash-occurrences",
          keywords: ["Unidash"],
          visible: () => anyOf("seeUnidashOccurrences"),
        },
        {
          label: "Mapa de Avarias",
          icon: "pi pi-fw pi-map",
          url: "/nok-map",
          keywords: ["Unidash mapa", "NOK"],
          visible: () => anyOf("accessUnidashNok"),
        },
      ]),
      group("Configurações", "pi pi-fw pi-cog", [
        {
          label: "Técnicos Agenda de Suporte",
          icon: "pi pi-fw pi-users",
          to: "/administration/support-extra-technicians",
          visible: () => anyOf("seeSupportExtraTechnicians"),
        },
        {
          label: "Preços de Survey",
          icon: "pi pi-fw pi-money-bill",
          to: "/administration/survey-pricing",
          visible: () => anyOf("seeSurveyPricingAdmin"),
        },
        {
          label: "Relatórios de Survey",
          icon: "fa-solid fa-file-lines",
          to: "/surveyReportConfigs",
          visible: () => anyOf("seeReportSurveyConfig"),
        },
      ]),
    ]),

    group(
      "Comercial",
      "pi pi-fw pi-briefcase",
      [
        {
          label: "Hub Comercial",
          icon: "pi pi-fw pi-chart-bar",
          to: "/commercial-hub",
          visible: () => anyOf("seeCommercialHub"),
        },
        group("CRM", "pi pi-fw pi-wallet", [
          {
            label: "CRM",
            icon: "pi pi-fw pi-wallet",
            to: "/crmV2",
            visible: () => anyOf("seeCRM"),
          },
          {
            label: "Gráficos",
            icon: "pi pi-fw pi-chart-bar",
            to: "/crm/graphics",
            visible: () => anyOf("seeCRMGraphics"),
          },
        ]),
        surveys,
        // One page with four steps (por classificar, classificações,
        // portfólio, revisão IA); the page hides the steps the user may
        // not open, so any of the three aliases shows the entry.
        {
          label: "Classificação de Negócios",
          icon: "pi pi-fw pi-check-square",
          to: "/business-classification",
          keywords: [
            "Classificar",
            "Portfólio",
            "Revisão IA",
            "Negócios por classificar",
            "Caso de estudo",
          ],
          visible: () =>
            anyOf(
              "seeBusinessClassification",
              "seeBusinessPortfolio",
              "autoClassifyBusiness"
            ),
        },
        group("Renovações", "pi pi-fw pi-paperclip", [
          {
            label: "Contratos a Expirar",
            icon: "pi pi-fw pi-paperclip",
            to: "/expiringContracts",
            visible: () => anyOf("seeExpiringContracts"),
          },
          {
            label: "Templates de Email",
            icon: "fa-solid fa-envelope-open-text",
            to: "/expiringContractsEmailsTemplates",
            visible: () => anyOf("seeExpiringContractsEmailTemplates"),
          },
        ]),
        group("Contactos Externos", "fa-regular fa-address-card", [
          {
            label: "Contactos",
            icon: "fa-regular fa-address-card",
            to: "/externalContacts",
            visible: () => anyOf("manageExternalContacts"),
          },
          {
            label: "Equipas",
            icon: "pi pi-fw pi-users",
            to: "/external-contact-teams",
            visible: () => anyOf("viewExternalContactTeams"),
          },
        ]),
        group("Comissões", "fa-solid fa-wallet", [
          {
            label: "Consulta",
            icon: "fa-solid fa-wallet",
            to: "/commissions-consultation",
            visible: () => anyOf("seeCommissionsReport"),
          },
        ]),
        {
          label: "Objetivos de Vendas",
          icon: "fa-solid fa-bullseye",
          to: "/salesObjectives",
          visible: () => anyOf("seeSalesObjectives"),
        },
        group("Configurações", "pi pi-fw pi-cog", [
          {
            label: "Parceiros",
            icon: "pi pi-fw pi-building",
            to: "/crm/admin/client-types",
            visible: () => anyOf("createCrmClientTypes"),
          },
          {
            label: "Categorias do CRM",
            icon: "pi pi-fw pi-tags",
            to: "/crm/admin/categories",
            keywords: ["Categorias"],
            visible: () => anyOf("createCrmCategories"),
          },
          {
            label: "Áreas de Negócio",
            icon: "pi pi-fw pi-briefcase",
            to: "/business-area",
            visible: () => anyOf("seeBusinessArea"),
          },
        ]),
      ]
    ),

    group("Financeiro", "fa-solid fa-coins", [
      group("Hub Financeiro", "pi pi-fw pi-chart-line", [
        {
          label: "W4M Solutions",
          icon: "pi pi-fw pi-chart-line",
          to: "/financial-hub",
          keywords: HUB_FINANCEIRO_KEYWORDS,
          visible: () => anyOf("seeFinancialHub"),
        },
        {
          label: "Ecomove",
          icon: "pi pi-fw pi-chart-line",
          to: "/ecomove/financial-hub",
          keywords: HUB_FINANCEIRO_KEYWORDS,
          visible: () => anyOf("seeEcomoveFinancialHub"),
        },
      ]),
      group("Gestão de Cobranças", "fa-solid fa-file-invoice-dollar", [
        {
          label: "W4M Solutions",
          icon: "fa-solid fa-file-invoice-dollar",
          to: "/invoicesManagement",
          keywords: ["Faturas"],
          visible: () => anyOf("seeInvoicesManagement"),
        },
        {
          label: "Ecomove",
          icon: "fa-solid fa-file-invoice-dollar",
          to: "/ecomove/invoicesManagement",
          visible: () => anyOf("seeEcomoveInvoicesManagement"),
        },
        // Uma entrada só: a empresa escolhe-se dentro da página.
        {
          label: "Templates de Emails",
          icon: "fa-solid fa-envelope-open-text",
          to: "/invoicesManagement/emailTemplates",
          keywords: ["Templates de Emails W4M", "Templates de Emails Ecomove"],
          visible: () =>
            anyOf(
              "seeInvoiceReminderEmailTemplates",
              "seeEcomoveInvoiceReminderEmailTemplates"
            ),
        },
      ]),
      // Installed deals still without the customer's purchase order,
      // chased the way the collections page chases invoices. W4M only.
      group("Gestão de Encomendas de Clientes", "fa-solid fa-file-signature", [
        {
          label: "Encomendas de Clientes",
          icon: "fa-solid fa-file-signature",
          to: "/customerOrdersManagement",
          keywords: ["Nota de Encomenda", "PO", "Instalado sem PO", "Negócios"],
          visible: () => anyOf("seeCustomerOrdersManagement"),
        },
        {
          label: "Templates de Emails",
          icon: "fa-solid fa-envelope-open-text",
          to: "/customerOrdersManagement/emailTemplates",
          keywords: ["Templates de Emails de Encomendas"],
          visible: () => anyOf("seeCustomerOrderEmailTemplates"),
        },
      ]),
      {
        label: "Emails de Clientes",
        icon: "fa-solid fa-address-book",
        to: "/clientEmails",
        keywords: ["Emails de Clientes Ecomove"],
        visible: () => anyOf("manageClientEmails", "manageEcomoveClientEmails"),
      },
    ]),

    group(
      "Operações",
      "pi pi-fw pi-cog",
      [
        {
          label: "Contratos",
          icon: "pi pi-list",
          to: "/contracts/list",
          visible: () => anyOf("seeContractsList"),
        },
        {
          label: "Entidades",
          icon: "pi pi-building",
          to: "/operational-management/entities",
          visible: () => anyOf("seeOperationalManagementMenu"),
        },
        {
          label: "Locais",
          icon: "pi pi-map-marker",
          to: "/operational-management/locals",
          visible: () => anyOf("seeOperationalManagementMenu"),
        },
        // Requested exception to "groups derive from their children": the
        // group also needs its own alias, and still hides when no map is
        // visible so it never opens empty.
        {
          ...group("Mapas Interativos", "pi pi-fw pi-map", interactiveMaps),
          visible: () =>
            anyOf("seeInteractiveMapsMenu") &&
            interactiveMaps.some(isItemVisible),
        },
        {
          label: "Material",
          icon: "pi pi-fw pi-box",
          to: "/material",
          visible: () =>
            anyOf(
              "seeMaterialToProcess",
              "seeMaterialToOrder",
              "seeMaterialToReceive",
              "seeMaterialToReceivePending",
              "seeMaterialReceived",
              "seeMaterialOrderVsPurchase"
            ),
        },
        {
          label: "Faturar Intervenções",
          icon: "pi pi-fw pi-euro",
          to: "/interventions-billing",
          visible: () => anyOf("seeBillInterventions"),
        },
        {
          label: "Pesquisar Produtos",
          icon: "pi pi-fw pi-search",
          to: "/product-search",
          visible: () => anyOf("seeSearchProducts"),
        },
        {
          label: "Dossiês Internos",
          icon: "pi pi-book",
          to: "/internal-dossiers",
          visible: () => anyOf("seeInternalDossiers"),
        },
        {
          label: "Guias de Transporte",
          icon: "fa-solid fa-cart-flatbed",
          to: "/transport-documents/locations",
          keywords: ["Cargas e Descargas"],
          visible: () => anyOf("seeTransportDocumentsLocations"),
        },
        group(
          "Fornecedores",
          "pi pi-fw pi-shopping-cart",
          [
            {
              label: "Fornecedores",
              icon: "pi pi-fw pi-building",
              to: "/suppliers",
              visible: () => anyOf("seeSuppliers"),
            },
            {
              label: "Encomendas",
              icon: "pi pi-fw pi-list",
              to: "/supplier-orders",
              visible: () => anyOf("seeSupplierOrders"),
            },
            {
              label: "Compras",
              icon: "pi pi-fw pi-shopping-bag",
              to: "/suppliers-purchasing",
              visible: () => anyOf("seeSuppliersPurchasing"),
            },
          ]
    ),
        group(
          "Licenças e Equipamentos",
          "pi pi-fw pi-sliders-h",
          [
            {
              label: "Gestão de Licenças",
              icon: "pi pi-fw pi-book",
              to: "/licenseManagement",
              visible: () => anyOf("seeLicenseManagement"),
            },
            {
              label: "Licenças por Tratar",
              icon: "fa-solid fa-award",
              to: "/licensesToProcess",
              visible: () => anyOf("seeLicensesToProcess"),
            },
            {
              label: "Equipamentos sem Local",
              icon: "fa-solid fa-thumbtack-slash",
              to: "/equipmentWithoutLocation",
              visible: () => anyOf("seeEquipmentWithoutLocation"),
            },
            {
              label: "Contratos Inativos com Equipamentos Ativos",
              icon: "fa-solid fa-file-circle-xmark",
              to: "/inactiveContractsWithActiveEquipments",
              visible: () => anyOf("seeInactiveContractsWithActiveEquipments"),
            },
          ]
    ),
        group(
          "Frota",
          "pi pi-fw pi-car",
          [
            {
              label: "Veículos",
              icon: "pi pi-fw pi-car",
              to: "/vehicles",
              visible: () => anyOf("seeFleetVehicles"),
            },
            {
              label: "Targa Telematics",
              icon: "pi pi-fw pi-info",
              to: "/targa-telematics",
              visible: () => anyOf("seeTargaTelematics"),
            },
            {
              label: "Deslocações sem Intervenção",
              icon: "pi pi-fw pi-map-marker",
              to: "/fleet-unlinked-trips",
              visible: () => anyOf("seeFleetUnlinkedTrips"),
              keywords: ["viagens", "targa", "frota", "sem trabalho"],
            },
            {
              label: "Documentos de Frota",
              icon: "pi pi-fw pi-file",
              to: "/fleet-user-documents",
              visible: () =>
                anyOf("viewFleetUserDocuments", "manageFleetUserDocuments"),
            },
          ]
    ),
      ]
    ),

    group(
      "Recursos Humanos",
      "pi pi-fw pi-users",
      [
        {
          label: "Férias da Equipa",
          icon: "pi pi-fw pi-users",
          to: "/vacations-department",
          visible: () => anyOf("seeTeamVacation"),
        },
        {
          label: "Controlo de Marcação de Férias",
          icon: "pi pi-fw pi-table",
          to: "/vacations-collaborators-summary",
          visible: () => anyOf("seeVacationMap"),
        },
        {
          label: "Faltas da Equipa",
          icon: "pi pi-fw pi-list",
          to: "/miss-all",
          keywords: ["Todas", "Todas as Faltas"],
          visible: () => anyOf("seeOthersMiss"),
        },
        {
          label: "Assiduidade",
          icon: "pi pi-fw pi-calendar-times",
          to: "/attendance-map",
          visible: () => anyOf("seeAttendanceMap"),
        },
        {
          label: "Horários de Trabalho",
          icon: "fa-solid fa-user-clock",
          to: "/time-clock-report",
          visible: () => anyOf("seeTimeClockReport"),
        },
        group(
          "Formações",
          "pi pi-fw pi-book",
          [
            {
              label: "Necessidades de Formação",
              icon: "pi pi-fw pi-plus-circle",
              to: "/training-needs",
              visible: () => anyOf("seeTrainingNeeds"),
            },
            {
              label: "Formações",
              icon: "pi pi-fw pi-book",
              to: "/training",
              visible: () => anyOf("SeeTraining"),
            },
            {
              label: "Resumo",
              icon: "pi pi-fw pi-list",
              to: "/training-resume",
              visible: () => anyOf("seeTrainingResume"),
            },
          ]
    ),
        group(
          "Competências",
          "pi pi-fw pi-star",
          [
            {
              label: "Perfis",
              icon: "pi pi-fw pi-star",
              to: "/competency-profile",
              visible: () => anyOf("seeCompetencyProfile"),
            },
            {
              label: "Resumo",
              icon: "pi pi-fw pi-list",
              to: "/competency-profile-resume",
              visible: () => anyOf("seeCompetencyProfileResume"),
            },
          ]
    ),
        {
          label: "Fichas dos Colaboradores",
          icon: "pi pi-fw pi-users",
          to: "/users-info",
          visible: () => anyOf("seeUsersInfo"),
        },
        {
          label: "Equipamentos",
          icon: "pi pi-fw pi-mobile",
          to: "/employee-equipment",
          visible: () => anyOf("seeEmployeeEquipment", "manageEmployeeEquipment"),
        },
        {
          label: "Fichas de Entrevistas",
          icon: "pi pi-fw pi-paperclip",
          to: "/interview-sheet",
          visible: () => anyOf("seeInterviewSheet"),
        },
        {
          label: "Base Documental",
          icon: "pi pi-fw pi-file",
          to: "/document-management",
          keywords: ["Gestão Documental"],
          visible: () => anyOf("seeDocumentManagement"),
        },
        group("Configurações", "pi pi-fw pi-cog", [
          {
            label: "Feriados Municipais",
            icon: "pi pi-fw pi-calendar",
            to: "/municipal-holidays",
            visible: () => anyOf("seeMunicipalHolidays"),
          },
        ]),
      ]
    ),

    group("Relatórios", "fa-solid fa-chart-line", [
      group("Comercial", "fa-solid fa-gauge-high", [
        {
          label: "Painel de Vendas",
          icon: "fa-solid fa-gauge-high",
          to: "/commercialReport",
          keywords: ["Vendas"],
          visible: () => anyOf("seeComercialReport"),
        },
        {
          label: "Vendas por Vendedor",
          icon: "fa-solid fa-file-invoice",
          to: "/reports/sales-by-seller",
          visible: () =>
            anyOf(
              "seeSalesMonthlyFeesBySellerReport",
              "seeSalesSetupBySellerReport",
              "seeSalesAnnualBySellerReport"
            ),
        },
        {
          label: "Comparação Anual",
          icon: "fa-solid fa-chart-line",
          to: "/reports/annual-sales-comparison",
          visible: () => anyOf("seeAnnualSalesComparisonReport"),
        },
      ]),
      group("Financeiros", "fa-solid fa-sack-dollar", [
        {
          label: "Previsão Faturação / Dívida Clientes",
          icon: "fa-solid fa-file-invoice",
          to: "/reports/clients-total-debts",
          visible: () => anyOf("seeClientsTotalDebtsReport"),
        },
        {
          label: "Cobrança a Clientes",
          icon: "fa-solid fa-file-invoice",
          to: "/reports/clients-collection",
          visible: () => anyOf("seeClientsCollectionReport"),
        },
        {
          label: "Faturas Recebidas",
          icon: "fa-solid fa-file-invoice",
          to: "/reports/received-invoices",
          visible: () => anyOf("seeReceivedInvoicesReport"),
        },
        {
          label: "Negócios Adjudicados Não Faturados",
          icon: "fa-solid fa-file-invoice",
          to: "/reports/unbilled-adjudicated-deals",
          visible: () => anyOf("seeUnbilledAdjudicatedDealsReport"),
        },
        {
          label: "Despesas",
          icon: "fa-solid fa-receipt",
          to: "/reports/expenses",
          visible: () => anyOf("seeExpensesReport"),
        },
        {
          label: "Despesas Aprovadas por Colaborador",
          icon: "fa-solid fa-user-check",
          to: "/reports/approved-expenses-by-employee",
          visible: () => anyOf("seeApprovedExpensesReport"),
        },
        {
          label: "Contratos de Suporte",
          icon: "fa-solid fa-file-contract",
          to: "/reports/support-contracts",
          visible: () => anyOf("seeSupportContractsReport"),
        },
        {
          label: "Contratos Suporte - Previsão",
          icon: "fa-solid fa-table-cells",
          to: "/reports/support-contracts-forecast",
          visible: () => anyOf("seeSupportContractsReport"),
        },
        {
          label: "Contratos de Manutenção por Gestor",
          icon: "fa-solid fa-sitemap",
          to: "/reports/maintenance-contracts-by-management",
          visible: () => anyOf("seeMaintenanceContractsByManagementReport"),
        },
        {
          label: "Contratos VS Ganhos",
          icon: "fa-solid fa-scale-balanced",
          to: "/reports/contracts-vs-gains",
          visible: () => anyOf("seeContractsVsGainsReport"),
        },
        {
          label: "Dossiês VS Previsão",
          icon: "fa-solid fa-folder-open",
          to: "/reports/dossiers-vs-forecast",
          visible: () => anyOf("seeDossiersVsForecastReport"),
        },
        {
          label: "Vendas e Contratos por Segmento",
          icon: "fa-solid fa-chart-pie",
          to: "/reports/sales-contracts-by-segment",
          visible: () => anyOf("seeSalesContractsBySegmentReport"),
        },
        {
          label: "Dívidas com Fornecedores",
          icon: "fa-solid fa-hand-holding-dollar",
          to: "/reports/supplier-debts",
          visible: () => anyOf("seeSupplierDebtsReport"),
        },
        {
          label: "Compras a Fornecedores",
          icon: "fa-solid fa-cart-shopping",
          to: "/reports/supplier-purchases",
          visible: () => anyOf("seeSupplierPurchasesReport"),
        },
        {
          label: "Receitas e Custos por Categoria",
          icon: "fa-solid fa-scale-balanced",
          to: "/reports/revenue-cost-categories",
          visible: () => anyOf("seeRevenueCostCategoriesReport"),
        },
        {
          label: "Receita Recorrente por Cliente",
          icon: "fa-solid fa-arrows-rotate",
          to: "/reports/recurring-revenue-clients",
          visible: () => anyOf("seeRecurringRevenueClientsReport"),
        },
      ]),
      group("Técnicos", "fa-solid fa-screwdriver-wrench", [
        {
          label: "PATs Tipos de Problemas",
          icon: "pi pi-fw pi-chart-bar",
          to: "/reports/pat-problem-types",
          visible: () => anyOf("seePatProblemTypesMenu"),
        },
        {
          label: "PATs de Suporte e Tickets",
          icon: "fa-solid fa-headset",
          to: "/reports/support-pats-tickets",
          visible: () => anyOf("seeSupportPatsTicketsReport"),
        },
        {
          label: "Horas de Técnicos Inativos",
          icon: "pi pi-user-minus",
          to: "/hours-inactive-users",
          visible: () => anyOf("seeInactiveTechnicianHours"),
        },
      ]),
      group("Recursos Humanos", "pi pi-fw pi-users", [
        {
          label: "Relatório de Colaboradores",
          icon: "pi pi-fw pi-align-justify",
          to: "/human-resources-report",
          visible: () => anyOf("seeUsersReport"),
        },
        {
          label: "Relatório de Horas",
          icon: "pi pi-fw pi-clock",
          to: "/hours-report",
          visible: () => anyOf("seeHoursReport"),
        },
        {
          label: "Relatório de Despesas",
          icon: "pi pi-fw pi-chart-bar",
          to: "/expenditure-report",
          visible: () => anyOf("seeExpenditureApprove"),
        },
      ]),
    ]),

    group("Documentos", "pi pi-fw pi-folder-open", [
      {
        label: "Documentos Partilhados",
        icon: "pi pi-fw pi-share-alt",
        to: "/shared-documents",
      },
      {
        label: "Organizador de Ficheiros",
        icon: "pi pi-fw pi-folder",
        to: "/file-organizer",
        visible: () =>
          anyOf("accessDocumentFolders", "shareDocumentFolders"),
      },
      {
        label: "Manuais",
        icon: "pi pi-fw pi-book",
        to: "/manuals",
        visible: () => anyOf("seeManuals"),
      },
      {
        label: "RGPD",
        icon: "pi pi-fw pi-shield",
        to: "/rgpd-documents",
        visible: () => anyOf("viewRgpdDocuments", "manageRgpdDocuments"),
      },
      group("Configurações", "pi pi-fw pi-cog", [
        {
          label: "Categorias de Manuais",
          icon: "pi pi-fw pi-tags",
          to: "/manuals-categories",
          visible: () => anyOf("seeManuals"),
        },
      ]),
    ]),

    group(
      "W4M",
      "pi pi-fw pi-code",
      [
        {
          label: "Contactos",
          icon: "pi pi-fw pi-phone",
          to: "/contacts",
          visible: () => anyOf("seeContacts"),
        },
        {
          label: "Organograma",
          icon: "pi pi-fw pi-sitemap",
          to: "/organization-chart",
          visible: () => anyOf("seeOrganizationChart"),
        },
        {
          label: "Links Úteis",
          icon: "pi pi-fw pi-link",
          to: "/useful-links-users",
          visible: () => anyOf("seeUsefulLinks"),
        },
        {
          label: "Divulgações",
          icon: "pi pi-fw pi-discord",
          to: "/disclosure",
          visible: () => anyOf("seeDisclosure"),
        },
        {
          label: "ISO",
          icon: "pi pi-fw pi-tag",
          url: "https://w4mds.sharepoint.com/:f:/t/W4M/EuT51yg73WxLq0nfeHsCpRIBYcZl2vdXiSN-l8JSq2lGhA?e=Fxy8Gs",
          target: "_blank",
          // Não tinha condição própria: quem via o grupo W4M via o ISO.
          visible: () => anyOf("seeW4M"),
        },
        group("Configurações", "pi pi-fw pi-cog", [
          {
            label: "Projetos do Organograma",
            icon: "pi pi-fw pi-cog",
            to: "/organization-chart/admin",
            visible: () =>
              anyOf("manageOrganogramaProjects", "manageUserProjects"),
          },
          {
            label: "Gerir Links Úteis",
            icon: "pi pi-fw pi-link",
            to: "/useful-links",
            keywords: ["Links Úteis"],
            visible: () =>
              anyOf("seeManagementUsefulLinks", "seeBusinessArea"),
          },
          {
            label: "Categorias de Links",
            icon: "pi pi-fw pi-tags",
            to: "/useful-links-categories",
            visible: () =>
              anyOf("seeManagementUsefulLinks", "seeBusinessArea"),
          },
        ]),
      ]
    ),

    group(
      "Administração",
      "pi pi-fw pi-sliders-v",
      [
        group(
          "ACLs",
          "pi pi-lock-open",
          [
            {
              label: "ACLs",
              icon: "pi pi-fw pi-eye",
              to: "/acls",
              visible: () => anyOf("seeAcls"),
            },
            {
              label: "Categorias",
              icon: "pi pi-fw pi-tag",
              to: "/acls-categories",
              visible: () => anyOf("seeAclsCategories"),
            },
            {
              label: "Grupos",
              icon: "pi pi-fw pi-th-large",
              to: "/acls-groups",
              visible: () => anyOf("seeAclsGroups"),
            },
            {
              label: "Utilizadores",
              icon: "pi pi-fw pi-users",
              to: "/acls-users",
              visible: () => anyOf("viewUserGroupAndAcls"),
            },
          ]
    ),
        {
          label: "Gerir Indicadores",
          icon: "pi pi-fw pi-chart-bar",
          to: "/administration/indicators",
          visible: () => anyOf("manageIndicators"),
        },
        group(
          "Notificações",
          "pi pi-fw pi-bell",
          [
            {
              label: "Prioridades",
              icon: "pi pi-fw pi-flag",
              to: "/notifications/admin/priorities",
              visible: () => anyOf("seeNotificationPriorities"),
            },
            {
              label: "Etiquetas",
              icon: "pi pi-fw pi-tags",
              to: "/notifications/admin/tags",
              visible: () => anyOf("seeNotificationTags"),
            },
            {
              label: "Organização",
              icon: "pi pi-fw pi-sitemap",
              to: "/notifications/admin/organization",
              visible: () => anyOf("organizeAlertConfigurations"),
            },
            {
              label: "Tipos de Notificação",
              icon: "pi pi-fw pi-list",
              to: "/notifications/admin/alerts",
              visible: () => anyOf("seeAlertConfigurations"),
            },
            {
              label: "Histórico",
              icon: "pi pi-fw pi-inbox",
              to: "/notifications/admin/current",
              visible: () => anyOf("seeCurrentNotifications"),
            },
          ]
    ),
        group("Integrações", "fa-solid fa-robot", [
          {
            label: "Tokens de Acesso",
            icon: "fa-solid fa-square-binary",
            to: "/tokens-access",
            visible: () => anyOf("manageTokensAccessMenu"),
          },
          {
            label: "Alterações de Produtos",
            icon: "pi pi-fw pi-check-square",
            to: "/product-changes",
            visible: () => anyOf("approveProductChanges"),
            keywords: ["produtos", "api", "aprovar", "phc", "stock", "marca", "modelo"],
          },
          {
            label: "Integrações de IA",
            icon: "fa-solid fa-robot",
            to: "/admin/ai",
            visible: () => anyOf("manageKiloAuthentication"),
            keywords: [
              "autenticação kilo",
              "kilo",
              "maestro",
              "bootstrap",
              "ia",
              "inteligência artificial",
              "api key",
            ],
          },
          {
            label: "Base de Conhecimento",
            icon: "pi pi-fw pi-database",
            to: "/admin/knowledge-base",
            visible: () => anyOf("managePatKnowledgeBase"),
            keywords: ["druid", "pats", "conhecimento", "json"],
          },
        ]),
        group("Extranet", "pi pi-fw pi-globe", [
          {
            label: "Utilizadores",
            icon: "pi pi-fw pi-users",
            to: "/extranet-users",
            visible: () => anyOf("seeExtranetUsers"),
          },
          {
            label: "Acessos Temporários",
            icon: "pi pi-fw pi-clock",
            to: "/extranet-temp-access",
            visible: () => anyOf("seeExtranetTempAccess"),
          },
        ]),
        {
          label: "Histórico de Emails",
          icon: "pi pi-fw pi-envelope",
          to: "/administration/email-history",
          visible: () => anyOf("seeEmailHistory"),
        },
        {
          label: "Registo de Auditoria",
          icon: "pi pi-fw pi-history",
          to: "/administration/audit-log",
          visible: () => anyOf("seeAuditLog"),
          keywords: ["auditoria", "audit", "histórico de alterações", "logs"],
        },
      ]
    ),
  ];
}

/**
 * Aliases que deixaram de ter efeito nesta reorganização
 * ------------------------------------------------------
 * Com a visibilidade dos grupos derivada dos filhos, os aliases que só serviam
 * para abrir/fechar um grupo tornaram-se inertes. Nenhum deles dava acesso a um
 * destino (verificado: um utilizador com apenas um destes aliases não alcançava
 * nenhuma página), pelo que ninguém perde acesso — mas continuam atribuídos em
 * `group_alcs` e podem ser desatribuídos quando alguém tratar disso:
 *
 *   seeSupportCommercial, seeHelpdeskMenu, seeSatisfactionSurveys,
 *   seeCommercialMenu, seeOperationalManagementMenu (mantém-se como alias dos
 *   itens Entidades/Locais), seeSupplierMenu, seeTransportDocuments, seeFleet,
 *   viewLicenseMenu, seeHumanResources, seeVacationMenu, seeExpenditureMenu,
 *   SeeMissMenu, seeExtraHoursMenu, seeTrainingMenu, seeMenuCompetencyProfile,
 *   seeW4M (passou a proteger o item ISO), seeAdministrationMenu, seeAclsMenu,
 *   seeNotificationsAdministration, seeCrmAdministration, seeExtranetMenu
 *
 * Os aliases dos 8 sub-relatórios financeiros cujos itens estavam comentados
 * também saíram — eram eles que faziam "Relatórios › Financeiros" abrir vazio:
 *
 *   seeOutstandingInvoicesReport, seePartiallyInvoicedSoldDealsReport,
 *   seeRentingDealsReport, seeInstalledWithPoDealsReport,
 *   seeInstalledWithoutPoDealsReport, seeNotInstalledWithPoDealsReport,
 *   seeNotInstalledWithoutPoDealsReport, seeContractsPendingRenewalReport
 *
 * Os relatórios em si continuam alcançáveis como drill-down de
 * `/reports/clients-total-debts`.
 */
